import { Divider, Stack, Typography } from '@mui/material';
import { SpaceDto } from 'tdc-web-backend/spaces/schemas';
import { UserRoles } from 'tdc-web-backend/enums/enums';
import { createSearchParams, useSearchParams } from 'react-router-dom';
import useGetMany from '../../../utils/hooks/crud-hooks/useGetMany';
import { Space } from './Space';
import CustomButton from '../../../components/button/CustomButton';
import { ReactComponent as SupportIcon } from '../../../assets/icons/chat-icons/support.svg';
import { ReactComponent as RobotIcon } from '../../../assets/icons/chat-icons/robot.svg';
import SupportForm from './SupportForm';
import AiAssistantHistory from './AiAssistantHistory';
import useAuth from '../../../utils/hooks/useAuth';
import { APPBAR_HEIGHT } from '../../../layout/dashboard-navigation/NavigationDashboard';
import { useEffect } from 'react';
import axios from '../../../api/axios';
import { useAiChatStore } from '../useAiChatStore';

export default () => {
  const {
    hasRole,
    authData: { userData },
  } = useAuth();
  const { setConversations, activeConversationId, setActiveConversation } = useAiChatStore();

  const isSponaAdmin = hasRole(UserRoles.Admin);
  let [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!userData?.id) return;

    const load = async () => {
      try {
        const sessionsResp = await axios.get('/ai/sessions', {
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': String(userData.id),
          },
        });

        const sessions = sessionsResp.data ?? [];

        const sessionsWithMessages = await Promise.all(
          sessions.map(async (s: any) => {
            try {
              const msgsResp = await axios.get(
                `/ai/sessions/${s.id}/messages`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'X-User-ID': String(userData.id),
                  },
                },
              );

              const msgs = (msgsResp.data ?? [])
                .map((m: any) => ({
                  id: String(m.id),
                  role: (m.role ?? 'assistant') as 'assistant' | 'user',
                  content: m.content ?? '',
                  timestamp: m.created_at ? new Date(m.created_at) : new Date(),
                }))
                .sort((a: any, b: any) => a.timestamp.getTime() - b.timestamp.getTime());

              return {
                id: String(s.id),
                title: s.title ?? 'AI Conversation',
                messages: msgs,
                createdAt: s.created_at ? new Date(s.created_at) : new Date(),
                updatedAt: s.updated_at
                  ? new Date(s.updated_at)
                  : s.created_at
                  ? new Date(s.created_at)
                  : new Date(),
              };
            } catch {
              return {
                id: String(s.id),
                title: s.title ?? 'AI Conversation',
                messages: [],
                createdAt: s.created_at ? new Date(s.created_at) : new Date(),
                updatedAt: s.updated_at ? new Date(s.updated_at) : new Date(),
              };
            }
          }),
        );

        setConversations(sessionsWithMessages);

        if (!activeConversationId && sessionsWithMessages.length > 0) {
          const latest = [...sessionsWithMessages].sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          )[0];
          setActiveConversation(latest.id);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load AI sessions in SpaceList:', err);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const { data } = useGetMany<SpaceDto>({
    resource: 'spaces',
  });
  const spaces = data?.data.results ?? [];

  return (
    <Stack
      width="375px"
      height={`calc(100vh - ${APPBAR_HEIGHT}px)`}
      direction="column"
      sx={{ backgroundColor: 'primaryDark.150', overflow: 'hidden' }}
    >
      {searchParams.get('showSupport') === 'true' ? (
        <SupportForm />
      ) : searchParams.get('showAiAssistant') === 'true' ? (
        <AiAssistantHistory />
      ) : (
        <Stack>
          <Stack px={3} py={1} direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="heading4" color="primaryDark.600">
              Spaces
            </Typography>
          </Stack>
          <Divider color="secondaryBlue.100" />
          <Stack direction="row" justifyContent="space-between" />
          {spaces.map((space) => (
            <Space space={space} />
          ))}

          <>
            {/* Always show AI assistant entry */}
            <Stack direction="row" px={3} py={2}>
              <CustomButton
                variant="primaryText"
                disableRipple
                onClick={() => {
                  setSearchParams(
                    createSearchParams({
                      showAiAssistant: 'true',
                    }).toString(),
                  );
                }}
              >
                <Stack direction="row" gap={1} alignItems="center">
                  <RobotIcon />
                  <Typography variant="heading4" color="primaryDark.600">
                    Project Assistant
                  </Typography>
                </Stack>
              </CustomButton>
            </Stack>

            {/* Keep support behind the admin gate (optional) */}
            {!isSponaAdmin && (
              <Stack direction="row" px={3} py={2}>
                <CustomButton
                  variant="primaryText"
                  disableRipple
                  onClick={() => {
                    setSearchParams(
                      createSearchParams({
                        showSupport: 'true',
                      }).toString(),
                    );
                  }}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <SupportIcon />
                    <Typography variant="heading4" color="primaryDark.600">
                      support
                    </Typography>
                  </Stack>
                </CustomButton>
              </Stack>
            )}
          </>
        </Stack>
      )}
    </Stack>
  );
};
